import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { getTask } from "../api/admin";
import { useNow } from "../api/jobs";
import { keys } from "../api/queries";
import {
  dismissSimJob,
  jobHome,
  jobPercent,
  SIM_JOB_TIMEOUT_MS,
  SIM_PENDING_TIMEOUT_MS,
  updateSimJob,
  useSimJobs,
  type SimJob,
} from "../api/simJobs";
import type { SimCaseProgress } from "../api/types";
import { elapsed } from "../lib/format";
import { SECTION_LABELS } from "../lib/sim";

const POLL_MS = 2000;

// Mounted once in the app shell: polls every running generation, whatever page is open.
export function SimJobWatcher() {
  const jobs = useSimJobs();
  return (
    <>
      {jobs.filter((j) => !j.outcome).map((j) => <SimTaskPoller key={j.taskId} job={j} />)}
    </>
  );
}

function SimTaskPoller({ job }: { job: SimJob }) {
  const qc = useQueryClient();
  const task = useQuery({
    queryKey: keys.task(job.taskId),
    queryFn: () => getTask(job.taskId),
    refetchInterval: (q) => (q.state.data?.ready ? false : POLL_MS),
    refetchIntervalInBackground: true,
    retry: 2,
  });

  useEffect(() => {
    const t = task.data;
    const finish = (ok: boolean, error: string | null, scenarioId = job.scenarioId) => {
      updateSimJob(job.taskId, { outcome: { ok, error, finishedAt: Date.now() }, scenarioId });
      qc.invalidateQueries({ queryKey: ["sim"] });
    };
    if (task.error) {
      finish(false, `Couldn't read the job's status (${task.error.message}).`);
      return;
    }
    if (!t) return;
    if (t.ready) {
      if (t.state === "SUCCESS") {
        const id = typeof t.result?.scenario_id === "number" ? t.result.scenario_id : job.scenarioId;
        finish(true, null, id);
      } else {
        finish(false, t.error ?? (t.state === "REVOKED" ? "The job was cancelled." : "Generation failed."));
      }
      return;
    }
    const progress = t.state === "PROGRESS" && t.result ? (t.result as unknown as SimCaseProgress) : null;
    updateSimJob(job.taskId, { state: t.state, ...(progress ? { progress } : {}) });

    const waited = Date.now() - job.startedAt;
    if (t.state === "PENDING" && waited > SIM_PENDING_TIMEOUT_MS) {
      finish(false, "No worker picked up the job. Check that the Celery worker is running, then try again.");
    } else if (waited > SIM_JOB_TIMEOUT_MS) {
      finish(false, "Stopped waiting for the job. Check the scenarios list before trying again.");
    }
  }, [task.data, task.dataUpdatedAt, task.error, job.taskId, job.scenarioId, job.startedAt, qc]);

  return null;
}

const STEPS: { stage: SimCaseProgress["stage"]; label: string }[] = [
  { stage: "sources", label: "Load approved course content" },
  { stage: "writing", label: "Write the case" },
  { stage: "saving", label: "Save the draft" },
];

function headline(job: SimJob) {
  if (job.outcome) return job.outcome.ok ? (job.kind === "generate" ? "Case ready" : "Regenerated") : "Failed";
  if (job.state === "PENDING") return "Waiting for the worker…";
  return job.progress?.message ?? "Starting…";
}

// Stepper + bar for one job. Used inline on the generate page and editor,
// and in the floating tray everywhere else.
export function SimJobProgress({ job, compact = false }: { job: SimJob; compact?: boolean }) {
  const now = useNow(!job.outcome);
  const p = job.progress;
  const currentIndex = job.outcome?.ok
    ? STEPS.length
    : p
      ? STEPS.findIndex((s) => s.stage === (p.stage === "repairing" ? "writing" : p.stage))
      : -1;
  const failed = job.outcome && !job.outcome.ok;
  const percent = jobPercent(job);
  const end = job.outcome?.finishedAt ?? now;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className={`font-medium ${failed ? "text-red-700" : ""}`}>{headline(job)}</span>
        <span className="shrink-0 tabular-nums text-xs text-gray-500">{elapsed(job.startedAt, end)}</span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded bg-gray-100" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <div
          className={`h-full rounded transition-all duration-700 ${failed ? "bg-red-500" : job.outcome ? "bg-green-600" : "bg-gray-800"}`}
          style={{ width: `${failed ? 100 : percent}%` }}
        />
      </div>
      {failed && job.outcome?.error && <p className="mt-2 break-words text-sm text-red-700">{job.outcome.error}</p>}
      {!compact && !failed && (
        <ol className="mt-3 space-y-1.5 text-sm">
          {STEPS.map((step, i) => {
            const done = i < currentIndex;
            const active = i === currentIndex && !job.outcome;
            return (
              <li key={step.stage} className={`flex items-start gap-2 ${done ? "text-gray-700" : active ? "text-gray-900" : "text-gray-400"}`}>
                <span className="mt-0.5 w-4 shrink-0 text-center" aria-hidden>
                  {done ? "✓" : active ? <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-gray-800" /> : "·"}
                </span>
                <span>
                  {step.label}
                  {active && step.stage === "sources" && p?.passages !== undefined && (
                    <span className="text-gray-500"> · {p.passages} passages from {p.documents} documents</span>
                  )}
                  {active && step.stage === "writing" && p && (
                    <span className="block text-xs text-gray-500">
                      {p.stage === "repairing"
                        ? "Fixing problems in the AI's output"
                        : p.current_section
                          ? `${SECTION_LABELS[p.current_section] ?? p.current_section}${(p.sections_total ?? 1) > 1 ? ` · section ${(p.sections_done ?? 0) + 1} of ${p.sections_total}` : ""}`
                          : "Waiting for the AI to start"}
                      {p.characters ? ` · ${p.characters.toLocaleString()} characters so far` : ""}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// Bottom-right stack of running/finished generations, hidden on the page
// that already shows that job inline.
export function SimJobTray() {
  const jobs = useSimJobs();
  const { pathname } = useLocation();
  const visible = jobs.filter((j) => jobHome(j) !== pathname);
  if (!visible.length) return null;
  return (
    <div className="fixed bottom-4 right-4 z-40 w-80 space-y-2" aria-live="polite">
      {visible.map((job) => (
        <div key={job.taskId} className="rounded-lg border bg-white p-3 shadow-lg">
          <div className="mb-2 flex items-start justify-between gap-2">
            <div className="min-w-0 text-xs text-gray-500">
              <div className="truncate">
                {job.kind === "generate" ? "Generating case" : job.section ? `Regenerating ${SECTION_LABELS[job.section]}` : "Regenerating case"}
              </div>
              <div className="truncate font-medium text-gray-700">{job.label}</div>
            </div>
            {job.outcome && (
              <button onClick={() => dismissSimJob(job.taskId)} className="text-sm text-gray-400 hover:text-gray-900" aria-label="Dismiss">✕</button>
            )}
          </div>
          <SimJobProgress job={job} compact />
          {job.outcome?.ok && job.scenarioId !== null && (
            <Link
              to={`/sim-patients/${job.scenarioId}/edit`}
              onClick={() => dismissSimJob(job.taskId)}
              className="mt-2 inline-block text-sm font-medium text-blue-700 hover:underline"
            >
              Review the case →
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}
